import { computed, defineComponent } from 'vue'
import type { IDataTableRow } from './types'
import { DataColumn } from '../data-column'
import { BODY_ROW_HEIGHT_PX, HEAD_ROW_HEIGHT_PX } from '../../constants'
import { tv } from 'tailwind-variants'
import { uref } from '@/utils/reactive/uref'
import type { DataTableRowMouseContext } from '../../types'
import { reactiveComputed } from '@vueuse/core'

const clazz = tv({
  base: 'border-t border-b border-gray-200 relative',
  variants: {
    head: {
      true: 'border-t border-b border-gray-200'
    },
    clickable: {
      true: 'cursor-pointer hover:shadow-inner transition-all'
    },
    selected: {
      true:'bg-blue-50'
    }
  }
})

export const DataRow = defineComponent<IDataTableRow & { borderlessTop?: boolean; }>(
  (props) => {
    const row = computed(() => uref(props.row))
    const columns = computed(() => row.value.columns || [])
    const height = computed(() => (props.head ? HEAD_ROW_HEIGHT_PX : BODY_ROW_HEIGHT_PX))
    const clickable = computed(() => (row.value.onClick ? true : false))

    const rowContext = reactiveComputed<DataTableRowMouseContext>(() => {
      return {
        hoveredColumns: {},
        hoveredColumn: -1,
        numColumns: columns.value.length
      }
    })

    const handleMouseEnterColumn = (index: number) => {
      if (!clickable.value) return

      rowContext.hoveredColumns[index] = true
      rowContext.hoveredColumn = index
    }

    const handleMouseLeaveColumn = (index: number) => {
      if (!clickable.value) return

      rowContext.hoveredColumns[index] = false
    }

    const handleClick = () => {
      if (!clickable.value) return

      if (row.value.onClick) {
        row.value.onClick(rowContext)
      }
      
    }

    return () => {
      return (
        <tr
          data-cy="data-row"
          onClick={handleClick}
          class={clazz({
            head: !!props.head,
            //color: props.variant as any,
            clickable: clickable.value,
            selected: props.selected
          })}
          style={{
            minHeight: height.value + 'px',
            height: height.value + 'px',
            maxHeight: height.value + 'px',
          }}
        >
          {columns.value.map((col, i) => {
            return (
              <DataColumn
                onMouseenter={() => handleMouseEnterColumn(i)}
                onMouseleave={() => handleMouseLeaveColumn(i)}
                table={props.table}
                borderless={props.borderless}
                borderlessTop={props.borderlessTop}
                head={props.head}
                disableSorting={props.disableSorting}
                column={col}
                key={`${row.value?.id}-${i}`}
              />
            )
          })}
        </tr>
      )
    }
  },
  { props: ['row', 'head', 'variant', 'borderless', 'table', 'disableSorting','selected'] }
)
